/**
 * ProjectManagerSettings.jsx — CRUD de Project Managers no painel de admin.
 */

import React, { useEffect, useState } from "react";
import {
  fetchProjectManagers,
  createProjectManager,
  updateProjectManager,
  deleteProjectManager,
} from "../../services/api";

export default function ProjectManagerSettings() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [editando, setEditando] = useState(null); // { id, nome, email } | null
  const [form, setForm] = useState({ nome: "", email: "" });
  const [modoForm, setModoForm] = useState(false); // true = novo PM
  const [salvando, setSalvando] = useState(false);

  const carregar = () => {
    setLoading(true);
    fetchProjectManagers()
      .then(setLista)
      .catch((e) => setErro(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, []);

  const iniciarNovo = () => {
    setEditando(null);
    setForm({ nome: "", email: "" });
    setModoForm(true);
  };

  const iniciarEdicao = (pm) => {
    setEditando(pm);
    setForm({ nome: pm.nome, email: pm.email || "" });
    setModoForm(false);
  };

  const cancelar = () => {
    setEditando(null);
    setModoForm(false);
    setErro(null);
  };

  const guardar = async () => {
    if (!form.nome.trim()) { setErro("O nome é obrigatório."); return; }
    setSalvando(true);
    setErro(null);
    try {
      const payload = { nome: form.nome.trim(), email: form.email.trim() || null };
      if (modoForm) {
        await createProjectManager(payload);
      } else {
        await updateProjectManager(editando.id, payload);
      }
      cancelar();
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (pm) => {
    if (!window.confirm(`Remover "${pm.nome}"? Os planners associados ficarão sem PM.`)) return;
    await deleteProjectManager(pm.id);
    carregar();
  };

  const emEdicao = (pm) => editando?.id === pm.id;

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.titulo}>Project Managers</h2>
        <button style={styles.btnNovo} onClick={iniciarNovo}>+ Novo PM</button>
      </div>

      {erro && <div style={styles.erro}>{erro}</div>}

      {/* Formulário novo / edição inline */}
      {(modoForm || editando) && (
        <div style={styles.formCard}>
          <h3 style={styles.formTitulo}>{modoForm ? "Novo Project Manager" : `Editar: ${editando.nome}`}</h3>
          <div style={styles.formGrid}>
            <div style={styles.formGrupo}>
              <label style={styles.label}>Nome *</label>
              <input
                style={styles.input}
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Nome completo"
              />
            </div>
            <div style={styles.formGrupo}>
              <label style={styles.label}>Email</label>
              <input
                style={styles.input}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@exemplo.com"
                type="email"
              />
            </div>
          </div>
          <div style={styles.formActions}>
            <button style={styles.btnGuardar} onClick={guardar} disabled={salvando}>
              {salvando ? "A guardar…" : "Guardar"}
            </button>
            <button style={styles.btnCancelar} onClick={cancelar}>Cancelar</button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={styles.loading}>A carregar…</p>
      ) : lista.length === 0 ? (
        <p style={styles.semDados}>Nenhum Project Manager criado ainda.</p>
      ) : (
        <table style={styles.tabela}>
          <thead>
            <tr>
              <th style={styles.th}>Nome</th>
              <th style={styles.th}>Email</th>
              <th style={{ ...styles.th, width: 140 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((pm) => (
              <tr key={pm.id} style={emEdicao(pm) ? styles.trEditando : styles.tr}>
                <td style={styles.td}>{pm.nome}</td>
                <td style={styles.td}>{pm.email || "—"}</td>
                <td style={styles.td}>
                  <div style={styles.acoes}>
                    <button style={styles.btnEditar} onClick={() => iniciarEdicao(pm)}>Editar</button>
                    <button style={styles.btnRemover} onClick={() => remover(pm)}>Remover</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  titulo: { fontSize: 16, fontWeight: 600, margin: 0, color: "#1e293b" },
  btnNovo: { padding: "8px 16px", background: "#6366f1", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  formCard: { background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20, marginBottom: 20 },
  formTitulo: { fontSize: 14, fontWeight: 600, margin: "0 0 16px", color: "#374151" },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 },
  formGrupo: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: "#475569" },
  input: { padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13 },
  formActions: { display: "flex", gap: 8 },
  btnGuardar: { padding: "8px 20px", background: "#22c55e", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnCancelar: { padding: "8px 16px", background: "none", color: "#64748b", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, cursor: "pointer" },
  tabela: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "8px 12px", backgroundColor: "#f8fafc", fontWeight: 600, borderBottom: "2px solid #e2e8f0", color: "#475569" },
  tr: { borderBottom: "1px solid #f1f5f9" },
  trEditando: { borderBottom: "1px solid #f1f5f9", backgroundColor: "#fefce8" },
  td: { padding: "10px 12px", color: "#374151" },
  acoes: { display: "flex", gap: 6 },
  btnEditar: { padding: "4px 12px", background: "#e0e7ff", color: "#4338ca", border: "none", borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnRemover: { padding: "4px 12px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  semDados: { color: "#94a3b8", fontSize: 14 },
  loading: { color: "#64748b", fontSize: 14 },
  erro: { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: 12, color: "#dc2626", marginBottom: 16, fontSize: 13 },
};
